import { type ChangeEvent, type FunctionComponent } from "react";
import Grid from "@mui/material/Grid";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import moment, { type Moment } from "moment-timezone";

import { FormControlWrapper } from "components/Shared";
import { TextBox, DateInput } from "components/Shared/Inputs";
import { NAME_MAX_LENGTH, PHONE_MAX_LENGTH, EMAIL_MAX_LENGTH } from "../../RiskAssessments.constants";

// Personal info (name/dob/phone/email) only exists on the standalone, non-AU RiskAssessments
// participant — the AU-embedded risk assessment doesn't repeat these (they live on the parent
// IndividualSupportSession participant), so this section is prop-driven rather than reusing
// the shared RiskAssessmentFieldActions context.
export interface PersonalInfoSectionParticipant {
  name: string;
  dateOfBirth: string | null;
  phoneNumber: string;
  emailAddress: string;
}

export interface PersonalInfoSectionProps {
  basePath: string;
  participant: PersonalInfoSectionParticipant;
  touched?: Record<string, unknown>;
  errors?: Record<string, string>;
  isReadonly: boolean;
  patchRecord: (partial: Partial<PersonalInfoSectionParticipant>) => void;
}

export const PersonalInfoSection: FunctionComponent<PersonalInfoSectionProps> = ({
  basePath,
  participant,
  touched,
  errors,
  isReadonly,
  patchRecord,
}) => {
  const { t } = useTranslation("criticalIncident");
  const { setFieldValue } = useFormikContext();

  const updateField = (field: keyof PersonalInfoSectionParticipant, value: unknown) => {
    setFieldValue(`${basePath}.${field}`, value);
    patchRecord({ [field]: value } as Partial<PersonalInfoSectionParticipant>);
  };

  const makeChangeHandler = (field: keyof PersonalInfoSectionParticipant) =>
    (event: ChangeEvent<HTMLInputElement>) => updateField(field, event.target.value);

  const handleDateOfBirthChange = (value: Moment | null) => updateField("dateOfBirth", value?.toDate().toISOString() || "");

  return (
    <>
      <Grid item sm={6}>
        <FormControlWrapper isReadonly={isReadonly} label={t("name")} name={`${basePath}.name`} fullWidth>
          <TextBox
            label={t("name")}
            name={`${basePath}.name`}
            value={participant.name}
            onChange={makeChangeHandler("name")}
            error={Boolean(touched?.name) && Boolean(errors?.name)}
            helperText={touched?.name ? t(errors?.name || " ") : ""}
            slotProps={{ input: { maxLength: NAME_MAX_LENGTH } }}
          />
        </FormControlWrapper>
      </Grid>
      <Grid item sm={6}>
        <FormControlWrapper
          isReadonly={isReadonly}
          type="date"
          dateFormat="L"
          label={t("date_of_birth")}
          name={`${basePath}.dateOfBirth`}
        >
          <DateInput
            label={t("date_of_birth")}
            name={`${basePath}.dateOfBirth`}
            onChange={handleDateOfBirthChange}
            value={participant.dateOfBirth ? moment(participant.dateOfBirth) : null}
            error={Boolean(touched?.dateOfBirth) && Boolean(errors?.dateOfBirth)}
            helperText={touched?.dateOfBirth ? t(errors?.dateOfBirth || " ") : ""}
            fullWidth
          />
        </FormControlWrapper>
      </Grid>
      <Grid item sm={6}>
        <FormControlWrapper isReadonly={isReadonly} label={t("phone")} name={`${basePath}.phoneNumber`} fullWidth>
          <TextBox
            label={t("phone")}
            name={`${basePath}.phoneNumber`}
            value={participant.phoneNumber}
            onChange={makeChangeHandler("phoneNumber")}
            error={Boolean(touched?.phoneNumber) && Boolean(errors?.phoneNumber)}
            helperText={touched?.phoneNumber ? t(errors?.phoneNumber || " ") : ""}
            slotProps={{ input: { maxLength: PHONE_MAX_LENGTH } }}
          />
        </FormControlWrapper>
      </Grid>
      <Grid item sm={6}>
        <FormControlWrapper isReadonly={isReadonly} label={t("email")} name={`${basePath}.emailAddress`} fullWidth>
          <TextBox
            label={t("email")}
            name={`${basePath}.emailAddress`}
            value={participant.emailAddress}
            onChange={makeChangeHandler("emailAddress")}
            error={Boolean(touched?.emailAddress) && Boolean(errors?.emailAddress)}
            helperText={touched?.emailAddress ? t(errors?.emailAddress || " ") : ""}
            slotProps={{ input: { maxLength: EMAIL_MAX_LENGTH } }}
          />
        </FormControlWrapper>
      </Grid>
    </>
  );
};
